import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StructuralUnitCard } from 'types/structural-unit.interface';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { BaseFormModalComponent } from '../base-form-modal.component';
import { NgClass } from '@angular/common';
import { ToastService } from 'services/toast.service';
import {
  emailValidator,
  getControlsWithErrors,
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_CODE,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_PHONE,
  phoneValidator
} from 'util/form.utility';

@Component({
  selector: 'app-edit-structural-unit',
  standalone: true,
  imports: [TranslateModule, FormsModule, SpinnerComponent, ReactiveFormsModule, NgClass],
  templateUrl: './edit-structural-unit.component.html',
  styleUrl: './edit-structural-unit.component.scss'
})
export class EditStructuralUnitComponent extends BaseFormModalComponent implements OnInit {
  @Input() structuralUnit!: StructuralUnitCard;
  @Input() isParent = false;
  @Input() override isLoading = false;
  @Output() save: EventEmitter<StructuralUnitCard> = new EventEmitter<StructuralUnitCard>();

  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get code(): AbstractControl<any, any> | null {
    return this.form.get('code');
  }
  get phone(): AbstractControl<any, any> | null {
    return this.form.get('phone');
  }
  get email(): AbstractControl<any, any> | null {
    return this.form.get('email');
  }
  get address(): AbstractControl<any, any> | null {
    return this.form.get('address');
  }
  get applicationEmail(): AbstractControl<any, any> | null {
    return this.form.get('applicationEmail');
  }
  get isDefaultStructureForIncomingDocuments(): AbstractControl<any, any> | null {
    return this.form.get('isDefaultStructureForIncomingDocuments');
  }

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngOnInit() {
    this.fillForm();
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      name: [this.structuralUnit.name, [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      code: [this.structuralUnit.code, [Validators.required, Validators.maxLength(MAX_LENGTH_CODE)]],
      phone: [
        this.structuralUnit.phone || '',
        [Validators.required, phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]
      ],
      email: [
        this.structuralUnit.email || '',
        [Validators.required, emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
      ],
      address: [this.structuralUnit.address || '', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDRESS)]],
      applicationEmail: [
        this.structuralUnit.applicationEmail || '',
        [Validators.required, emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
      ],
      isDefaultStructureForIncomingDocuments: [this.structuralUnit.isDefaultStructureForIncomingDocuments || false]
    });
  }

  handleSave(): void {
    if (this.form.valid) {
      this.submitAttempted = false;
      this.save.emit({
        ...this.structuralUnit,
        name: this.name?.value,
        code: this.code?.value,
        phone: this.phone?.value || null,
        email: this.email?.value || null,
        address: this.address?.value || null,
        applicationEmail: this.applicationEmail?.value || null,
        isDefaultStructureForIncomingDocuments: this.isDefaultStructureForIncomingDocuments?.value || false
      });
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.editDepartment');
    }
  }

  protected override close(): void {
    this.name?.setValue(this.structuralUnit.name);
    this.code?.setValue(this.structuralUnit.code);
    this.phone?.setValue(this.structuralUnit.phone || '');
    this.email?.setValue(this.structuralUnit.email || '');
    this.address?.setValue(this.structuralUnit.address || '');
    this.applicationEmail?.setValue(this.structuralUnit.applicationEmail || '');
    this.isDefaultStructureForIncomingDocuments?.setValue(this.structuralUnit.isDefaultStructureForIncomingDocuments || false);
    super.close();
  }
}
